/**
 * Post Create Form
 *
 * Form-state adapter for the post create screen.
 */

import { getImageHandler } from "../../util/media/image/image-setup";
import { FormBase } from "../../util/form-base-form";
import { BoardId } from "../../constants/board-id";
import { Patterns } from "../../constants/patterns";
import { Roles } from "../../constants/roles";
import type { ImagePreviewData, VideoPreviewData } from "./post-create-api";

export type PostCreateImageItem = ImagePreviewData & {
    data?: File;
};

export type PostCreateVideoItem = VideoPreviewData;

type ImageManagerFormItem = {
    data?: File | null;
};

interface PostCreateFormState {
    title: string;
    content: string;
    boardId: string | null;
}

type FormFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type DomLookupRoot = ParentNode | Event | EventTarget | Element | null | undefined;

class PostCreateForm extends FormBase {
    public images: PostCreateImageItem[];
    public videos: PostCreateVideoItem[];
    public rating: number;
    public translationEnabled: boolean;
    public formData: PostCreateFormState;

    public constructor() {
        super();
        this.images = [];
        this.videos = [];
        this.rating = 0;
        this.translationEnabled = false;
        this.formData = {
            title: "",
            content: "",
            boardId: null,
        };
    }

    public async init(): Promise<void> {
        this.initializeBoardId();
        return Promise.resolve();
    }

    public getPostCreateForm(root: DomLookupRoot = document): HTMLFormElement | null {
        if (root instanceof HTMLFormElement) {
            return root;
        }

        return this.getForm(root, `[data-role="${Roles.POST_CREATE_FORM}"]`);
    }

    public getImageUpload(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.IMAGE_MANAGER}"] [data-role="${Roles.IMAGE_UPLOAD}"]`,
            HTMLInputElement,
        );
    }

    public getVideoUpload(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.VIDEO_MANAGER}"] [data-role="${Roles.VIDEO_UPLOAD}"]`,
            HTMLInputElement,
        );
    }

    public getAddImageButton(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.IMAGE_MANAGER}"] [data-role="${Roles.PICKER_ADD}"]`,
            HTMLElement,
        );
    }

    public getImageList(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.IMAGE_LIST}"], [data-role="${Roles.IMAGE_MANAGER}"] [data-role="${Roles.IMAGE_GRID}"]`,
            HTMLElement,
        );
    }

    public getVideoList(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.VIDEO_LIST}"], [data-role="${Roles.VIDEO_MANAGER}"] [data-role="${Roles.VIDEO_GRID}"]`,
            HTMLElement,
        );
    }

    public getPreviewRoot(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PREVIEW_ROOT}"]`, HTMLElement);
    }

    public getTitleField(root: ParentNode): HTMLInputElement | HTMLTextAreaElement | null {
        return (
            this.getInput(root, `[data-role="${Roles.POST_TITLE}"], input[name="post_title"]`) ||
            this.getTextarea(root, 'textarea[name="post_title"]')
        );
    }

    public hasEmbeddedMedia(root: ParentNode): boolean {
        return this.queryElement(root, "img, video, iframe, audio, source", HTMLElement) instanceof HTMLElement;
    }

    public getInvalidElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".is-invalid", HTMLElement);
    }

    public getContentField(root: ParentNode): HTMLTextAreaElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_CONTENT}"], textarea[name="content"]`,
            HTMLTextAreaElement,
        );
    }

    public getRatingSlider(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(root, `[data-role="${Roles.RATING_SLIDER}"], #rating-slider`, HTMLInputElement);
    }

    public getTranslationToggle(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.TRANSLATION_TOGGLE}"], #translation-toggle`,
            HTMLInputElement,
        );
    }

    public getSubmitTrigger(root: ParentNode): HTMLButtonElement | HTMLInputElement | null {
        return (
            this.getButton(root, `[data-role="${Roles.POST_SUBMIT}"], button[type="submit"]`) ||
            this.getInput(root, 'input[type="submit"]')
        );
    }

    public getPreviewButton(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.PREVIEW_BTN}"], #preview-btn`, HTMLElement);
    }

    public getAddVideoButton(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BTN_ADD_VIDEOS}"], #btn-add-videos`, HTMLElement);
    }

    public getReviewAgreement(root: DomLookupRoot): HTMLInputElement | null {
        return this.queryElement(root, `input[data-role="${Roles.REVIEW_AGREEMENT}"]`, HTMLInputElement);
    }

    public getShareToTikTokToggle(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[name="sns_shares[]"][value="tiktok"]');
    }

    public getShareToInstagramToggle(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[name="sns_shares[]"][value="instagram"]');
    }

    public getSnsLinksList(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LINKS_LIST}"]`, HTMLElement);
    }

    public getYoutubeSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.YOUTUBE_LINKS_CONTAINER}"]`, HTMLElement);
    }

    public getYoutubeContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.YOUTUBE_LINKS_LIST}"]`, HTMLElement);
    }

    public getSnsPreviewContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LIVE_PREVIEW}"]`, HTMLElement);
    }

    public getOgpPreviewContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.OGP_LIVE_PREVIEW}"]`, HTMLElement);
    }

    public getSnsPreviewTab(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_PREVIEW_TAB}"]`, HTMLElement);
    }

    public getOgpPreviewTab(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.OGP_PREVIEW_TAB}"]`, HTMLElement);
    }

    public getSnsLinksPanel(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LINKS_PANEL}"][data-post-id]`, HTMLElement);
    }

    public getProductSummaryDetails(root: ParentNode): HTMLDetailsElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.POST_CREATE_PRODUCT_SUMMARY_DETAILS}"]`,
            HTMLDetailsElement,
        );
    }

    public getProductSummaryToggle(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `:scope > [data-role="${Roles.POST_CREATE_PRODUCT_SUMMARY_TOGGLE}"]`,
            HTMLElement,
        );
    }

    public getProductSummaryBody(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `:scope > [data-role="${Roles.POST_CREATE_PRODUCT_SUMMARY_BODY}"]`, HTMLElement);
    }

    public getProductSummaryBodyInner(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CREATE_PRODUCT_SUMMARY_BODY_INNER}"]`, HTMLElement);
    }

    public getBackNumberToggle(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(root, 'input[type="checkbox"][data-back-number-toggle="1"]', HTMLInputElement);
    }

    public getCreatedAtInput(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            'input[type="date"][name="created_at"][data-back-number-date="1"]',
            HTMLInputElement,
        );
    }

    public getPreviewTab(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[role="tab"][data-role="${Roles.POST_TAB}"][data-tab="preview"]`, HTMLElement);
    }

    public getFormTab(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[role="tab"][data-role="${Roles.POST_TAB}"][data-tab="form"]`, HTMLElement);
    }

    public getPreviewPanel(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PANEL}"][data-panel="preview"]`, HTMLElement);
    }

    public getBoardIdInput(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[name="board_id"]');
    }

    public getProductIdInput(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[name="post_product_id"]');
    }

    public getReviewProductIdInput(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[name="review_data[post_product_id]"]');
    }

    public getEventTarget(root: DomLookupRoot): Element | null {
        return this.fromEventTarget(root, Element);
    }

    public getRatingStar(root: DomLookupRoot): HTMLElement | null {
        const target = this.getEventTarget(root);
        const starIcon = target instanceof HTMLElement ? target : null;

        if (starIcon?.dataset.role === Roles.RATING_STAR) {
            return starIcon;
        }

        return this.closest(starIcon, `[data-role="${Roles.RATING_STAR}"]`, HTMLElement);
    }

    public getRatingInput(root: DomLookupRoot): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.RATING_INPUT}"]`, HTMLElement);
    }

    public getReviewAnswerInput(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(root, `[data-role="${Roles.REVIEW_ANSWER}"]`, HTMLInputElement);
    }

    public getHiddenNamedInput(root: DomLookupRoot): HTMLInputElement | null {
        return this.getInput(root, 'input[type="hidden"][name]');
    }

    public getEventRadio(root: DomLookupRoot): HTMLInputElement | null {
        return this.closest(this.getEventTarget(root), 'input[type="radio"]', HTMLInputElement);
    }

    public getReviewOptionLabels(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.REVIEW_OPTION_LABEL}"]`, HTMLElement);
    }

    public getCheckedRadio(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[type="radio"]:checked');
    }

    public getReviewQuestionLabels(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.REVIEW_QUESTION_LABEL}"]`, HTMLElement);
    }

    public getReviewSingleSelectWrappers(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.REVIEW_SINGLE_SELECT}"]`, HTMLElement);
    }

    public getReviewOptionTexts(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.REVIEW_OPTION_TEXT}"][data-option-index]`, HTMLElement);
    }

    public getReviewMultiSelects(root: ParentNode): HTMLSelectElement[] {
        return this.queryElements(root, 'select[data-review-multi-select="1"]', HTMLSelectElement);
    }

    public getRatingStars(root: ParentNode | null | undefined): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.RATING_STAR}"]`, HTMLElement);
    }

    public getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"][name="sns_links[]"]`, HTMLInputElement);
    }

    public getWysiwygContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_CONTENT}"]`, HTMLElement);
    }

    public getUserStarsPreview(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.USER_STARS_PREVIEW}"]`, HTMLElement);
    }

    public getUserRatingAverage(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.USER_RATING_AVERAGE}"]`, HTMLElement);
    }

    public getUserRatingCount(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.USER_RATING_COUNT}"]`, HTMLElement);
    }

    public getMediaRemoveButton(root: DomLookupRoot): HTMLButtonElement | null {
        return this.getButton(root, `[data-role="${Roles.MEDIA_REMOVE}"]`);
    }

    public getAllRatingAnswerInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.RATING_INPUT}"] [data-role="${Roles.REVIEW_ANSWER}"]`,
            HTMLInputElement,
        );
    }

    public getReviewAnswerFields(root: ParentNode): FormFieldElement[] {
        return this.queryElements(root, '[name^="review_data[answers]"]', HTMLElement).filter((field) =>
            this.isFormFieldElement(field),
        );
    }

    public getFieldsByName(root: ParentNode, name: string): FormFieldElement[] {
        return this.queryElements(root, `[name="${CSS.escape(name)}"]`, HTMLElement).filter((field) =>
            this.isFormFieldElement(field),
        );
    }

    public getPreviewCleanupElements(root: ParentNode): HTMLElement[] {
        return [
            this.queryElement(root, `[data-role="${Roles.PREVIEW_TITLE}"]`, HTMLElement),
            this.queryElement(root, ".attachment-outline", HTMLElement),
            this.queryElement(root, ".preview-images", HTMLElement),
            this.queryElement(root, ".preview-videos", HTMLElement),
        ].filter((element): element is HTMLElement => element instanceof HTMLElement);
    }

    public getChoicesHost(root: DomLookupRoot): ParentNode | null {
        return (
            this.closest(root, `[data-role="${Roles.CHOICES_FIELD}"]`, HTMLElement) ||
            this.closest(root, `[data-role="${Roles.CHOICES_CONTROL}"]`, HTMLElement) ||
            (root instanceof Element ? root.parentNode : null)
        );
    }

    public getSpinnerElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".spinner", HTMLElement);
    }

    public initializeBoardId(): void {
        const pathParts = window.location.pathname.split("/");
        const createIndex = pathParts.findIndex((part) => part === "create");

        if (createIndex !== -1 && pathParts[createIndex - 1]) {
            this.formData.boardId = pathParts[createIndex - 1];
        }

        const metaElement = this.queryElement(document, 'meta[name="board-id"]', HTMLMetaElement);
        if (metaElement instanceof HTMLMetaElement && !this.formData.boardId) {
            this.formData.boardId = metaElement.getAttribute("content");
        }
    }

    public setTitle(title: string): void {
        this.formData.title = title;
    }

    public setContent(content: string): void {
        this.formData.content = content;
    }

    public setRating(rating: number): void {
        this.rating = rating;
    }

    public setTranslationEnabled(enabled: boolean): void {
        this.translationEnabled = enabled;
    }

    public getImages(): PostCreateImageItem[] {
        return [...this.images];
    }

    public addImages(images: PostCreateImageItem[]): void {
        this.images.push(...images);
    }

    public removeImage(imageId: number): void {
        this.images = this.images.filter((img) => img.id !== imageId);
    }

    public getVideos(): PostCreateVideoItem[] {
        return [...this.videos];
    }

    public addVideos(videos: PostCreateVideoItem[]): void {
        this.videos.push(...videos);
    }

    public removeVideo(videoId: number): void {
        this.videos = this.videos.filter((video) => video.id !== videoId);
    }

    public getBoardIdFromPath(): string {
        const pathname = window.location.pathname;
        const match = pathname.match(Patterns.BOARD_ID_CREATE_PATH) || pathname.match(Patterns.BOARD_ID_BOARD_PATH);
        if (match) {
            return match[1];
        }

        const fallbackMatch = pathname.match(Patterns.PATHNAME_POSTS_ID);
        return fallbackMatch ? fallbackMatch[1] : BoardId.REVIEW;
    }

    public getReviewQuestionsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".review-questions-section", HTMLElement);
    }

    public getReviewQuestion(root: DomLookupRoot): HTMLElement | null {
        return this.closest(root, ".review-question", HTMLElement);
    }

    public getFirstReviewQuestion(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".review-question", HTMLElement);
    }

    /** Returns the target element only if it IS the .review-question itself (not a child). */
    public getReviewQuestionAsEventTarget(root: DomLookupRoot): HTMLElement | null {
        const target = this.getEventTarget(root);
        if (!(target instanceof HTMLElement)) return null;
        const question = this.closest(target, ".review-question", HTMLElement);
        return question === target ? question : null;
    }

    public getRatingInputFromQuestion(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.RATING_INPUT}"]`, HTMLElement);
    }

    public getNextReviewQuestion(el: HTMLElement | null): HTMLElement | null {
        if (!(el instanceof HTMLElement)) return null;
        const next = el.nextElementSibling;
        return next instanceof HTMLElement ? next : null;
    }

    public getPreviousReviewQuestion(el: HTMLElement | null): HTMLElement | null {
        if (!(el instanceof HTMLElement)) return null;
        const prev = el.previousElementSibling;
        return prev instanceof HTMLElement ? prev : null;
    }

    public getFirstReviewQuestionAnswer(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.RATING_STAR}"], textarea[data-role="${Roles.REVIEW_ANSWER}"], [data-role="${Roles.CHOICES_CONTROL}"]`,
            HTMLElement,
        );
    }

    public getElementModalBody(root: Element): HTMLElement | null {
        return this.closest(root, ".element-modal-body", HTMLElement);
    }

    public getClosestForm(el: Element): HTMLFormElement | null {
        return this.closest(el, "form", HTMLFormElement);
    }

    public getWysiwygContentEditor(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".ql-editor", HTMLElement);
    }

    public getFormData(formEl: HTMLFormElement | null = null): FormData {
        const formData = new FormData();

        const form = (formEl instanceof HTMLFormElement && formEl) || this.getPostCreateForm(document);

        if (!form) {
            formData.append("board_id", this.getBoardIdFromPath());
            return formData;
        }

        const boardIdFromForm = this.getBoardIdInput(form)?.value;
        const boardIdFromDataset = form.getAttribute("data-board-id") || form.dataset?.boardId;
        const contentTextareaForBoard = this.getContentField(form);
        const boardIdFromTextarea = contentTextareaForBoard?.getAttribute("data-board-id");
        const boardId = boardIdFromForm || boardIdFromDataset || boardIdFromTextarea || this.getBoardIdFromPath();
        formData.append("board_id", boardId);

        if (form.classList.contains("logout-form") || form.action.includes("logout")) {
            const postForm = this.getForm(document, `[data-role="${Roles.POST_CREATE_FORM}"]`);
            if (!(postForm instanceof HTMLFormElement) || postForm.classList.contains("logout-form")) {
                return formData;
            }
        }

        Array.from(form.elements).forEach((element) => {
            if (!this.isFormFieldElement(element)) {
                return;
            }

            if (!element.name || element.disabled) {
                return;
            }

            if (
                element.name === "rating" ||
                element.name === "translation_enabled" ||
                element.name === "_back_number" ||
                element.name === "review_agreement" ||
                element.name === "product_data"
            ) {
                return;
            }

            if (element instanceof HTMLInputElement) {
                if (element.type === "file") return;

                if (element.type === "radio" || element.type === "checkbox") {
                    if (element.checked) {
                        formData.append(element.name, element.value);
                    }
                    return;
                }

                if (element.name === "content") {
                    return;
                }

                formData.append(element.name, element.value);
                return;
            }

            if (element instanceof HTMLSelectElement) {
                if (element.multiple) {
                    Array.from(element.selectedOptions).forEach((option) => {
                        formData.append(element.name, option.value);
                    });
                    return;
                }

                formData.append(element.name, element.value);
                return;
            }

            if (element.name === "content") {
                return;
            }

            formData.append(element.name, element.value);
        });

        let hasTitle = false;
        let hasContent = false;
        for (const [key, value] of formData.entries()) {
            if (key === "post_title" && value) hasTitle = true;
            if (key === "content" && value) hasContent = true;
        }

        if (!hasTitle) {
            const titleField =
                this.getInput(form, 'input[name="post_title"]') ||
                this.getTextarea(form, 'textarea[name="post_title"]');
            if (titleField) {
                formData.append(titleField.name || "post_title", titleField.value || "");
            }
        }

        if (!hasContent) {
            const contentTextarea = this.queryElement(
                form,
                `[data-role="${Roles.POST_CONTENT}"], textarea[name="content"]`,
                HTMLTextAreaElement,
            );

            if (contentTextarea instanceof HTMLTextAreaElement) {
                formData.append("content", contentTextarea.value || "");
            } else {
                const quillEditor = this.queryElement(form, `[data-role="${Roles.WYSIWYG_CONTENT}"]`, HTMLElement);
                if (quillEditor instanceof HTMLElement) {
                    formData.append("content", quillEditor.innerHTML || "");
                } else {
                    const contentField =
                        this.getTextarea(form, 'textarea[name="content"]') ||
                        this.getInput(form, 'input[name="content"]');
                    if (contentField) {
                        formData.append("content", contentField.value || "");
                    }
                }
            }
        }

        let imagesAdded = 0;
        let imageManagerData: ImageManagerFormItem[] | null = null;
        const possiblePostIds = ["create", "create-form", "post-create"];

        for (const postId of possiblePostIds) {
            const handler = getImageHandler(postId);
            if (!handler) continue;

            const data = handler.getFormData();
            if (Array.isArray(data) && data.length > 0) {
                imageManagerData = data;
                break;
            }
        }

        if (Array.isArray(imageManagerData)) {
            imageManagerData.forEach((imageData) => {
                const file = imageData?.data ?? null;
                if (file instanceof File) {
                    formData.append("attached_images[]", file);
                    imagesAdded++;
                }
            });
        }

        if (imagesAdded === 0) {
            const imageInput = this.getInput(document, 'input[name="images[]"]');
            const files = imageInput?.files || null;

            if (files) {
                Array.from(files).forEach((file) => {
                    if (file.type.startsWith("image/")) {
                        formData.append("attached_images[]", file);
                        imagesAdded++;
                    }
                });
            }
        }

        this.videos.forEach((video) => {
            if (video.file instanceof File) {
                formData.append("attached_videos[]", video.file);
            }
        });

        const contentImagesList = this.queryElement(
            document,
            `[data-role="${Roles.CONTENT_IMAGES_LIST}"][data-board-id="${boardId}"]`,
            HTMLElement,
        );
        if (contentImagesList instanceof HTMLElement) {
            const contentImages: string[] = [];
            const items = this.queryElements(contentImagesList, "li", HTMLLIElement);
            items.forEach((item) => {
                const filename = item.dataset.filename;
                if (filename) {
                    contentImages.push(filename);
                }
            });

            if (contentImages.length > 0) {
                formData.append("content_images", JSON.stringify(contentImages));
            }
        }

        return formData;
    }

    private isFormFieldElement(element: Element): element is FormFieldElement {
        return (
            element instanceof HTMLInputElement ||
            element instanceof HTMLSelectElement ||
            element instanceof HTMLTextAreaElement
        );
    }
}

const postCreateForm = new PostCreateForm();

export { PostCreateForm, postCreateForm };
